import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Info } from 'lucide-react'
import type { LearnGapField } from '../../utils/cardSideDisplay'
import { useLocale } from '../../i18n/context'

interface Props {
  /** The single next field the mark is asking for — never both at once. The
   * mark's own tap keeps whatever behaviour the surrounding card gives a tap
   * (this component attaches no handler of its own to the label), so it
   * still opens the card for editing exactly as it did before this mark
   * existed. Only the info icon below is this component's own affordance. */
  gapKind: LearnGapField
  /** The desktop list row runs this mark at 9.5px inside a tighter pill; the
   * card surfaces (collapsed row, expanded card, active card) share one
   * larger size. Layout only — the copy and behaviour are identical. */
  compact?: boolean
  /** Whether the info icon is a ⇥ stop. Default true — every surface that
   * shows one mark at a time wants it reachable. The desktop deck list passes
   * `false` for every row except its one roving tab stop: a list of a thousand
   * rows would otherwise put a thousand stops between ⇥ and the pane, which is
   * the same trap the row's own `tabIndex` roving removes (see
   * `DeckDesktopPanels`' `tabStopCardId`). Reachable there by selecting the
   * row first — ↑/↓ — after which this is the next stop. */
  tabStop?: boolean
}

/**
 * The not-in-study mark: a single next-step label plus a tap-only info icon
 * for the "why" behind it. Replaces a mark that used to state the barrier
 * and the remedy in one line — which produced nested quotation marks and an
 * overlong line in Russian — with two things read separately: a short next
 * step always visible, and an explanation the reader opens only if they want
 * it. No hover tooltip exists alongside the tap popover (there is only ever
 * one of these open at a time, and only from a tap).
 */
const POPOVER_WIDTH = 220
const EDGE_PAD = 8

export default function NotInStudyMark({ gapKind, compact = false, tabStop = true }: Props) {
  const { tr } = useLocale()
  const cb = tr.cardBubble
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLSpanElement | null>(null)
  const popoverRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) return
    function onDocPointerDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDocPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onDocPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  // Positions the popover imperatively (not via React state) — this is a
  // one-shot measure-then-place against the DOM, not state the app needs to
  // render from, so it skips the extra render a setState-in-effect would add.
  useLayoutEffect(() => {
    if (!open) return
    const rootEl = rootRef.current
    const popoverEl = popoverRef.current
    if (!rootEl || !popoverEl) return
    const cardEl = rootEl.closest('[data-vocab-card]') as HTMLElement | null
    const rootRect = rootEl.getBoundingClientRect()
    const popRect = popoverEl.getBoundingClientRect()
    const bounds = cardEl
      ? cardEl.getBoundingClientRect()
      : { left: 0, right: window.innerWidth }

    let next = 0
    const rightOverflow = (rootRect.left + popRect.width) - (bounds.right - EDGE_PAD)
    if (rightOverflow > 0) next -= rightOverflow
    const leftOverflow = (bounds.left + EDGE_PAD) - (rootRect.left + next)
    if (leftOverflow > 0) next += leftOverflow

    popoverEl.style.left = `${next}px`

    // Vertical: the default (set in the JSX below) opens downward from the
    // mark. Near the bottom of the viewport — the last row of a scrolled
    // list is the common case — that clips the popover against the window
    // edge. Flip to open upward, anchored to the mark's own top, whenever
    // the popover's actual measured height doesn't fit in the room below.
    const spaceBelow = window.innerHeight - rootRect.bottom - EDGE_PAD
    if (spaceBelow < popRect.height) {
      popoverEl.style.top = 'auto'
      popoverEl.style.bottom = '100%'
      popoverEl.style.marginTop = '0'
      popoverEl.style.marginBottom = '6px'
    } else {
      popoverEl.style.top = '100%'
      popoverEl.style.bottom = 'auto'
      popoverEl.style.marginTop = '6px'
      popoverEl.style.marginBottom = '0'
    }
  }, [open])

  return (
    <span ref={rootRef} style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
      <span
        style={{
          display: 'inline-flex', alignItems: 'center', gap: compact ? 4 : 6,
          background: 'var(--bg-secondary)', color: 'var(--text-secondary)',
          borderRadius: 'var(--radius-pill)', flexShrink: 0,
          padding: compact ? '1px 6px 1px 8px' : '5px 8px 5px 8px',
          fontFamily: 'var(--font-ui)', fontWeight: 600,
          fontSize: compact ? 9.5 : 11.5, whiteSpace: 'nowrap',
        }}
      >
        {/* One icon, and it is the trailing info button below — not two.
            A leading CircleMinus used to sit here as well, which put a mark
            on each side of the label: the reader had to work out that only
            one of them did anything. The label already states the next step,
            so a decorative glyph restating "something is missing" earns
            nothing and competes with the one glyph that is a control. */}
        {cb.notInStudyLabel(gapKind)}
        {/* The only interactive part of the mark that is NOT a pass-through
            to the card's own tap — stopPropagation here, and nowhere else in
            this component, is what keeps that distinction. */}
        <button
          type="button"
          tabIndex={tabStop ? 0 : -1}
          onClick={(e) => { e.stopPropagation(); setOpen((v) => !v) }}
          aria-label={cb.notInStudyInfoAria}
          aria-expanded={open}
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            background: 'none', border: 'none', padding: 0, marginLeft: 1,
            color: 'var(--text-secondary)', cursor: 'pointer', flexShrink: 0,
          }}
        >
          <Info size={compact ? 11 : 12} strokeWidth={2} />
        </button>
      </span>

      {open && (
        <div
          ref={popoverRef}
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'absolute', top: '100%', left: 0, marginTop: 6, zIndex: 20,
            width: POPOVER_WIDTH, background: 'var(--bg-surface)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-float)',
            padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 8,
          }}
        >
          <div style={{ fontFamily: 'var(--font-ui)', fontSize: 12, lineHeight: 1.45, color: 'var(--text-primary)' }}>
            {cb.notInStudyExplanation}
          </div>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setOpen(false) }}
            style={{
              alignSelf: 'flex-start', background: 'none', border: 'none', color: 'var(--accent)',
              fontFamily: 'var(--font-ui)', fontWeight: 600, fontSize: 12, cursor: 'pointer', padding: 0,
            }}
          >
            {cb.notInStudyExplanationDismiss}
          </button>
        </div>
      )}
    </span>
  )
}
